import { dateStrToAgeDays, dateStrToAgeMonths, Enum_Patientvisit_Status, Enum_Survey_Access_Restriction, getChecksum, notEmpty, PatientDobFormat, useGenerateDescriptiveReportMutation, useUpdatePatientVisitMutation } from '@cyren/common-lib';
import classNames from 'classnames';
import { memo, useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useStaticLocales } from '../../admin/hooks/locales/use-static-locale';
import Footer from '../../app/Footer';
import thumbsUp from '../../assets/ill/Thumbs_up.svg';
import LoadingAnim from '../../components/LoadingAnim';
import LogoPatientDynamic from '../../components/LogoPatientDynamic';
import PatientOrgContainer from '../hooks/PatientOrgContainer';
import { useOnNavChange } from '../../hooks/use-on-nav-change';
import { useData } from '../../report/use-data';
import { usePatientTransData } from '../../report/use-patient-trans-data';
import { useSurveyMeta } from '../../survey/use-survey-meta';
import { useTranslations } from '../hooks/use-translations';
import PaTreeHeader from '../tree/PaTreeHeader';
import { Button } from '@mui/material';
import { useNavigate } from 'react-router';
import { useRecoilState } from 'recoil';
import { PaReportState } from '../hooks/use-report';
import { TOptions } from '../pa-prop-types';
import { AthenaServices } from '../../lib/athena/athena-services';
import { useSurveyState, usePaSurveyParams } from '../hooks/use-survey-state';
import { getPatientBasePath } from '../../app/route-utils';
import ClinicalTrialActions from './clinical-trial/ClinicalTrialActions';
import ClinicalTrialAds from './clinical-trial/ClinicalTrialAds';
import ClinicalTrialCard from './clinical-trial/ClinicalTrialCard';
import ClinicalTrialInquiryModal from './clinical-trial/ClinicalTrialInquiryModal';

const collapCls =
  'collapse collapse-arrow border-2 border-black border-opacity-10 rounded-none first:rounded-t-xl last:rounded-b-xl border-t-0 first:border-t-2 font-base';
const collapHeaderCls = 'collapse-title text-base font-medium';
const collapBodyCls = 'collapse-content opacity-70 text-base';

function InlineProgressBar({ progress }: { progress: number }) {
  return (
    <div className='w-full h-2 bg-black/10 rounded-full overflow-hidden'>
      <div className='h-full bg-primary' style={{ width: `${Math.min(Math.max(progress, 0), 100)}%` }} />
    </div>
  );
}

function HospitalFAQSection({
  metaItems,
  t,
  tOptions,
  selectedFaqIndex,
  setSelectedFaqIndex,
}: {
  metaItems: Array<{ id: string; qnaQuestionKey: string; qnaAnswerKey: string }>;
  t: (key?: string | null, opts?: TOptions | null) => string | undefined;
  tOptions: TOptions;
  selectedFaqIndex: number | null;
  setSelectedFaqIndex: (idx: number | null) => void;
}) {
  if (!notEmpty(metaItems)) {
    return null;
  }

  return (
    <div className='flex-col py-4 w-full'>
      {metaItems?.map((metaItem, idx) => {
        const open = selectedFaqIndex === idx;
        return (
          <div
            key={metaItem.id}
            className={classNames(collapCls, open && 'collapse-open')}
            onClick={() => {
              if (open) {
                setSelectedFaqIndex(null);
              } else {
                setSelectedFaqIndex(idx);
              }
            }}
          >
            <div className={collapHeaderCls}>{t(metaItem.qnaQuestionKey, tOptions) ?? ''}</div>
            <div className={collapBodyCls}>
              <p>{t(metaItem.qnaAnswerKey, tOptions) ?? ''}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}


function PaSurveyEndPageContent() {
  const [
    {
      surveyId,
      dataState: { survey, visit },
    },
  ] = useData({ disableRedirectOnInvalidReport: true });

  const nav = useNavigate();
  const { orgId, shortId } = usePaSurveyParams();
  const baseUrl = `${getPatientBasePath(window.location.pathname)}/${orgId}/${shortId}`;
  const { sltStr } = useStaticLocales();
  const [, { t }] = useTranslations();
  const container = PatientOrgContainer.useContainer();

  const [allowNavigate, setAllowNavigate] = useState(false);
  const [selectedFaqIndex, setSelectedFaqIndex] = useState<number | null>(null);
  const [athenaIntegrationExecuted, setAthenaIntegrationExecuted] = useState(false);
  const [isAthenaProcessing, setIsAthenaProcessing] = useState(false);
  const [descriptiveNoteGenerated, setDescriptiveNoteGenerated] = useState(false);
  const [isWaitingListOpen, setIsWaitingListOpen] = useState(false);

  // reportState, surveyState
  const [reportState, setReportState] = useRecoilState(PaReportState);
  const [{ surveyState, visit: visitFromSurveyState }] = useSurveyState();

  const [ccdaTemp, setCcdaTemp] = useState<string>('');
  const isF1z7sna = shortId === 'f1z7sna' || shortId === 'o76xz7c' || shortId === 'u7etsub';

  const handleWaitingList = () => {
    setIsWaitingListOpen(true);
  };

  const [generateDescriptiveReport, { loading: loadingReport }] = useGenerateDescriptiveReportMutation({
    onError: (error) => {
      console.error('Error generating descriptive report:', error);
    },
  });

  const [updatePatientVisit] = useUpdatePatientVisitMutation({
    onError: (error) => {
      console.error('Error updating patient visit:', error);
    },
  });

  useOnNavChange({
    block: allowNavigate,
    onChange: () => {
      toast.info(sltStr({ key: 'msg-cannot-go-back' }), {
        toastId: 'msg-cannot-go-back',
      });
    },
  });

  const { transKeys, metaItems } = useSurveyMeta({
    surveyId,
    metaKey: 'faq',
  });

  const tOptions = (reportState?.systemValueMap ? Object.fromEntries(
    Object.entries(reportState?.systemValueMap).filter(([, value]) => typeof value === 'string'))
    : {}) as TOptions;
  const titleLabel = t('finish-page-title', tOptions, { fallbackValue: null });
  const subtitleLabel = t('finish-page-desc', tOptions, { fallbackValue: null });

  const pageKeys = survey?.metadata?.finishedPageKeys || [];
  const [{ loadingTrans }] = usePatientTransData({
    keys: [...pageKeys, ...transKeys],
  });

  // 설문 완료 페이지 접근 시 localStorage 정리
  useEffect(() => {
    // 설문 완료 페이지에 접근하면 모든 설문 관련 localStorage 정리
    try {
      // encounterCheck 제거
      localStorage.removeItem('encounterCheck');

      // recoil-persist로 저장된 설문 상태들도 정리
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.includes('AdminPatientSurveyState') || key.includes('PaReportState'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));

    } catch (error) {
      console.warn('localStorage 정리 중 오류:', error);
    }
  }, []);

  // 환자 방문이 완료되면 Athena check-in 실행
  useEffect(() => {
    const medication = surveyState?.medication;
    const medicationCurrent = surveyState?.currentMedication;
    const allergy = surveyState?.allergy;
    const allergyFinal = surveyState?.allergyFinal;
    const reaction = surveyState?.reaction ?? 'Other';
    const providerCCDA = surveyState?.providerCCDA;

    if (surveyState?.patientDetails && !athenaIntegrationExecuted) {
      localStorage.removeItem('encounterCheck');
      setIsAthenaProcessing(true);

      // Athena 통합 함수들을 순차적으로 실행
      const executeAthenaIntegration = async () => {
        try {
          // await sendDescriptiveNoteToAthena();
          // await handleAllergy();
          // await handleMedication();
          await handleProviderAndPharmacy();
          // Athena 통합 완료 후 PaSurveyTerminatedPage로 이동
          nav(`${baseUrl}/terminated`);
        } catch (error) {
          console.error('Athena 통합 중 오류 발생:', error);
          // 오류가 발생해도 terminated 페이지로 이동
          nav(`${baseUrl}/terminated`);
        } finally {
          setIsAthenaProcessing(false);
          setAthenaIntegrationExecuted(true);
        }
      };

      executeAthenaIntegration();
    }
  }, [surveyState?.patientDetails, athenaIntegrationExecuted]);

  // 설문 데이터에서 환자 정보 추출하는 헬퍼 함수
  const extractPatientInfoFromSurvey = (systemValueMap: any) => {
    if (!systemValueMap) return null;

    // 설문에서 환자 정보를 추출하는 로직
    // 다양한 필드명 패턴을 시도 (기본값 제거 - 실제 데이터만 사용)
    const patientInfo = {
      firstname: systemValueMap.firstName || systemValueMap.first_name || systemValueMap.firstname || systemValueMap.name?.split(' ')[0] || '',
      lastname: systemValueMap.lastName || systemValueMap.last_name || systemValueMap.lastname || systemValueMap.name?.split(' ')[1] || '',
      // age 필드도 DOB로 사용될 수 있음
      dob: systemValueMap.dateOfBirth || systemValueMap.dob || systemValueMap.birthDate || systemValueMap.birthday || systemValueMap.age || '',
      departmentid: systemValueMap.departmentId || systemValueMap.department_id || systemValueMap.departmentid || '',
      mobilephone: systemValueMap.mobilePhone || systemValueMap.phone || systemValueMap.mobile || systemValueMap.telephone || '',
      homephone: systemValueMap.homePhone || systemValueMap.homephone || '',
      workphone: systemValueMap.workPhone || systemValueMap.workphone || '',
    };

    return patientInfo;
  };

  // EMR 연동이 없을 때 Descriptive Note 생성 및 저장
  useEffect(() => {
    console.log('[PaSurveyEndPage] Checking conditions for descriptive note generation:', {
      hasPatientDetails: !!surveyState?.patientDetails,
      descriptiveNoteGenerated,
      visitUuid: visit?.uuid,
      visitId: visit?.id,
      hasDescriptiveReport: !!reportState?.descriptiveReport,
    });

    // Athena 연동이 있으면 (patientDetails가 있으면) 이 로직은 실행하지 않음
    // Athena 연동 시에는 백엔드에서 handleHpiUpdate를 통해 처리됨
    if (surveyState?.patientDetails) {
      console.log('[PaSurveyEndPage] Skipping: Athena integration detected');
      return;
    }

    // 이미 생성했거나, visit이 없으면 스킵
    if (descriptiveNoteGenerated || !visit?.uuid) {
      console.log('[PaSurveyEndPage] Skipping: Already generated or no visit uuid');
      return;
    }

    // reportState에 이미 descriptiveReport가 있으면 스킵
    if (reportState?.descriptiveReport) {
      console.log('[PaSurveyEndPage] Skipping: descriptiveReport already exists');
      return;
    }

    // 환자 정보 추출
    const patientInfo = extractPatientInfoFromSurvey(reportState?.systemValueMap);

    // 나이 계산
    let age: number | null = null;
    const dob = patientInfo?.dob || "";
    const monthAge = dateStrToAgeMonths(dob, {
      inputFormat: PatientDobFormat,
    });
    const dayAge = dateStrToAgeDays(dob, {
      inputFormat: PatientDobFormat,
    });

    if (dob) {
      const birthDate = new Date(dob);
      const today = new Date();
      const currentYear = today.getFullYear();
      const birthYear = birthDate.getFullYear();
      age = currentYear - birthYear;
      const monthDifference = today.getMonth() - birthDate.getMonth();
      const dayDifference = today.getDate() - birthDate.getDate();
      if (monthDifference < 0 || (monthDifference === 0 && dayDifference < 0)) {
        age--;
      }
    }

    // 성별 추출
    const gender = reportState?.systemValueMap?.sex?.toString().replace("o-", "") || 'Gender is Not provided';

    // 필요한 데이터 추출
    const cc = reportState?.systemValueMap?.cc || '';
    const treeStatesInfo = reportState?.treeStates || [];

    // 디버깅을 위해 treeStates 구조 확인

    // HPI 데이터 추출 - treeStates에서 answerMap을 통해 데이터 수집
    const hpiData: any[] = [];
    const allergyData: any[] = [];
    const pmhData: any[] = [];
    const medicationData: any[] = [];

    // 의료 줄임말을 표준 의학 용어로 변환하는 사전
    const medicalAbbreviations: { [key: string]: string } = {
      // 신체 부위
      'Abd': 'abdomen',
      'Abd Whole': 'whole abdomen',
      'Chest': 'chest',
      'Chest Rt Upper': 'right upper chest',
      'Chest Lt Upper': 'left upper chest',
      'Chest Rt Lower': 'right lower chest',
      'Chest Lt Lower': 'left lower chest',
      'Rt': 'right',
      'Lt': 'left',
      'Upper': 'upper',
      'Lower': 'lower',

      // 증상 관련
      'Abd Pain': 'abdominal pain',
      'Chest Pain': 'chest pain',
      'Groin Pain': 'groin pain',
      'Head Pain': 'headache',
      'Back Pain': 'back pain',

      // 시간 관련
      'Onset': 'onset',
      'Duration': 'duration',
      'Frequency': 'frequency',
      'Nature': 'nature',
      'Context': 'context',
      'Progression': 'progression',

      // 기타
      'PMH': 'past medical history',
      'PMHx': 'past medical history',
      'CC': 'chief complaint',
      'HPI': 'history of present illness'
    };

    // 의료 줄임말을 표준 용어로 변환하는 함수
    const convertMedicalAbbreviation = (text: string): string => {
      // 정확한 매치가 있으면 변환
      if (medicalAbbreviations[text]) {
        return medicalAbbreviations[text];
      }

      // 부분 매치가 있는지 확인 (예: "Abd Pain" → "abdominal pain")
      const found = Object.entries(medicalAbbreviations).find(([abbrev]) => text.includes(abbrev));
      if (found) {
        const [abbrev, full] = found;
        return text.replace(abbrev, full);
      }

      // 매치가 없으면 원본 반환
      return text;
    };

    treeStatesInfo.forEach((currentTreeState: any, index: number) => {

      if (currentTreeState?.answerMap) {
        Object.entries(currentTreeState.answerMap).forEach(([questionKey, answer]: [string, any]) => {

          // answerValues가 있는 경우도 처리
          if (answer?.answerValues && answer.answerValues.length > 0) {
            const value = answer.answerValues[0];
            if (value && typeof value === 'string' && value.trim()) {
              const currentTreeKey = currentTreeState.treeKey || 'unknown';

              // currentTreeKey에 따라 분류하고 더 적절한 라벨 사용
              if (currentTreeKey.toLowerCase().includes('allergy')) {
                // 알레르기 정보는 더 간단하게
                allergyData.push(`Allergy: ${value}`);
              } else if (currentTreeKey.toLowerCase().includes('pmh') ||
                currentTreeKey.toLowerCase().includes('history')) {
                // PMH 정보도 더 간단하게
                pmhData.push(`PMH: ${value}`);
              } else if (currentTreeKey.toLowerCase().includes('medication') ||
                currentTreeKey.toLowerCase().includes('med')) {
                // 약물 정보는 medicationData에 추가
                medicationData.push(`Medication: ${value}`);
              } else if (currentTreeKey.toLowerCase().includes('injury') ||
                currentTreeKey.toLowerCase().includes('pain') ||
                currentTreeKey.toLowerCase().includes('symptom')) {
                // 부상, 통증, 증상 관련 데이터도 HPI에 추가
                hpiData.push(`${currentTreeKey}: ${value}`);
              }
            }
          }

          // answerKeys가 있는 경우도 처리 (선택된 옵션들)
          if (answer?.answerKeys && answer.answerKeys.length > 0) {
            answer.answerKeys.forEach((key: string) => {
              // notesForKeys에서 해당 키의 노트 가져오기
              const noteForKey = answer?.notesForKeys?.[key];
              const hasNote = noteForKey && typeof noteForKey === 'string' && noteForKey.trim();

              if (key.startsWith('o-')) {
                const optionText = key.replace('o-', '').replace(/-/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
                const currentTreeKey = currentTreeState.treeKey || 'unknown';

                // currentTreeKey에 따라 분류
                if (currentTreeKey.toLowerCase().includes('pmh') ||
                  currentTreeKey.toLowerCase().includes('history')) {
                  // PMH 관련 answerKeys도 추가
                  const pmhText = hasNote
                    ? `PMH - ${questionKey.replace('q-', '').replace(/-/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}: ${optionText} (${noteForKey})`
                    : `PMH - ${questionKey.replace('q-', '').replace(/-/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}: ${optionText}`;
                  pmhData.push(pmhText);
                } else if (currentTreeKey.toLowerCase().includes('medication') ||
                  currentTreeKey.toLowerCase().includes('med')) {
                  // 약물 관련 answerKeys도 추가
                  const medText = hasNote
                    ? `Medication - ${questionKey.replace('q-', '').replace(/-/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}: ${optionText} (${noteForKey})`
                    : `Medication - ${questionKey.replace('q-', '').replace(/-/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}: ${optionText}`;
                  medicationData.push(medText);
                } else {
                  // HPI에 추가 (증상, 부상 등)
                  const hpiText = hasNote
                    ? `${questionKey.replace('q-', '').replace(/-/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}: ${optionText} (${noteForKey})`
                    : `${questionKey.replace('q-', '').replace(/-/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}: ${optionText}`;
                  hpiData.push(hpiText);
                }
              }

              if (key.startsWith('sx-')) {
                const optionText = key.replace('sx-', '').replace(/-/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
                const currentTreeKey = currentTreeState.treeKey || 'unknown';

                // currentTreeKey에 따라 분류
                if (currentTreeKey.toLowerCase().includes('allergy')) {
                  // 알레르기 관련 answerKeys도 추가
                  const allergyText = hasNote
                    ? `Allergy Reaction - ${questionKey.replace('q-', '').replace(/-/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}: ${optionText} (${noteForKey})`
                    : `Allergy Reaction - ${questionKey.replace('q-', '').replace(/-/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}: ${optionText}`;
                  allergyData.push(allergyText);
                } else {
                  // HPI에 추가 (증상 관련)
                  const hpiText = hasNote
                    ? `${questionKey.replace('q-', '').replace(/-/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}: ${optionText} (${noteForKey})`
                    : `${questionKey.replace('q-', '').replace(/-/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}: ${optionText}`;
                  hpiData.push(hpiText);
                }
              }
            });
          }
        });
      }

      // PMH 데이터 특별 처리 (pmhx-screen treeKey)
      if (currentTreeState.treeKey === 'pmhx-screen' && currentTreeState.answerMap) {
        Object.entries(currentTreeState.answerMap).forEach(([questionKey, answer]: [string, any]) => {
          // 질문 키를 사람이 읽기 쉬운 형태로 변환
          let questionText = questionKey.replace('q-', '').replace(/-/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());

          // 특정 질문들을 더 명확하게 변환
          if (questionKey === 'q-relevant-history') questionText = 'Relevant History';
          else if (questionKey.includes('q-medication-pta-time')) questionText = 'Medication Prior to Admission Time';
          else if (questionKey.includes('q-medication-pta')) questionText = 'Medication Prior to Admission';

          // answerValues가 있는 경우
          if (answer?.answerValues && answer.answerValues.length > 0) {
            const value = answer.answerValues[0];
            if (value && typeof value === 'string' && value.trim()) {
              pmhData.push(`PMH - ${questionText}: ${value}`);
            }
          }

          // answerKeys가 있는 경우 (선택된 옵션들)
          if (answer?.answerKeys && answer.answerKeys.length > 0) {
            answer.answerKeys.forEach((key: string) => {
              if (key.startsWith('o-')) {
                const optionText = key.replace('o-', '').replace(/-/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
                pmhData.push(`PMH - ${questionText}: ${optionText}`);
              }
            });
          }
        });
      }

      // 증상별 세부 데이터 수집 (sx-chestpain, sx-myalgia 등)
      if (currentTreeState.treeKey && currentTreeState.treeKey.startsWith('sx-')) {
        const symptomKey = currentTreeState.treeKey;
        const symptomName = symptomKey.replace('sx-', '').replace(/-/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());

        if (currentTreeState.answerMap) {
          Object.entries(currentTreeState.answerMap).forEach(([questionKey, answer]: [string, any]) => {
            // answerValues가 있는 경우 해당 값들을 증상과 연결하여 추가
            if (answer?.answerValues && answer.answerValues.length > 0) {
              const value = answer.answerValues[0];
              if (value && typeof value === 'string' && value.trim()) {
                // 질문 키를 사람이 읽기 쉬운 형태로 변환
                let questionText = questionKey.replace('q-', '').replace(/-/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());

                // 특정 질문들을 더 명확하게 변환
                if (questionKey === 'q-onset-datetime') questionText = 'Onset';
                else if (questionKey === 'q-sx-count') questionText = 'Frequency';
                else if (questionKey === 'q-sx-duration') questionText = 'Duration';
                else if (questionKey === 'q-sx-continuous-intermittent') questionText = 'Nature';
                else if (questionKey === 'q-chest-location') questionText = 'Location';
                else if (questionKey === 'q-abrupt-gradual') questionText = 'Onset Type';
                else if (questionKey === 'q-chest-event') questionText = 'Context';
                else if (questionKey === 'q-sx-aggravation') questionText = 'Progression';

                // 의료 줄임말 변환 적용
                const convertedSymptomName = convertMedicalAbbreviation(symptomName);
                const convertedQuestionText = convertMedicalAbbreviation(questionText);
                const convertedValue = convertMedicalAbbreviation(value);

                // 날짜 포맷: YYYY-MM-DD, YYYY-MM-DD HH:MM, YYYY-MM-DD HH
                // 날짜 변환 유틸리티
                const humanizeDateDifference = (dateStr: string): string => {
                  if (!dateStr) return dateStr;
                  const now = new Date();
                  let date;
                  let diffText = dateStr;

                  // YYYY-MM-DD HH:MM
                  const ymdhmMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})$/);
                  // YYYY-MM-DD HH
                  const ymdhMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2})$/);
                  // YYYY-MM-DD only
                  const ymdMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);

                  if (ymdhmMatch) {
                    // YYYY-MM-DD HH:MM
                    date = new Date(
                      Number(ymdhmMatch[1]), Number(ymdhmMatch[2]) - 1, Number(ymdhmMatch[3]),
                      Number(ymdhmMatch[4]), Number(ymdhmMatch[5])
                    );
                    const diffMs = now.getTime() - date.getTime();
                    if (diffMs < 60000) {
                      diffText = "Just now";
                    } else if (diffMs < 3600000) {
                      const min = Math.floor(diffMs / 60000);
                      diffText = `${min} minute${min === 1 ? '' : 's'} ago`;
                    } else if (diffMs < 86400000) {
                      const hours = Math.floor(diffMs / 3600000);
                      diffText = `${hours} hour${hours === 1 ? '' : 's'} ago`;
                    } else {
                      const days = Math.floor(diffMs / 86400000);
                      diffText = `${days} day${days === 1 ? '' : 's'} ago`;
                    }
                  } else if (ymdhMatch) {
                    // YYYY-MM-DD HH
                    date = new Date(
                      Number(ymdhMatch[1]), Number(ymdhMatch[2]) - 1, Number(ymdhMatch[3]),
                      Number(ymdhMatch[4]), 0
                    );
                    const diffMs = now.getTime() - date.getTime();
                    if (diffMs < 3600000) {
                      const min = Math.floor(diffMs / 60000);
                      diffText = `${min} minute${min === 1 ? '' : 's'} ago`;
                    } else if (diffMs < 86400000) {
                      const hours = Math.floor(diffMs / 3600000);
                      diffText = `${hours} hour${hours === 1 ? '' : 's'} ago`;
                    } else {
                      const days = Math.floor(diffMs / 86400000);
                      diffText = `${days} day${days === 1 ? '' : 's'} ago`;
                    }
                  } else if (ymdMatch) {
                    // YYYY-MM-DD
                    date = new Date(Number(ymdMatch[1]), Number(ymdMatch[2]) - 1, Number(ymdMatch[3]));
                    const diffMs = now.setHours(0, 0, 0, 0) - date.getTime(); // ignore hours/mins for date-only
                    const days = Math.floor(diffMs / 86400000);
                    if (days < 7) {
                      diffText = `${days} day${days === 1 ? '' : 's'} ago`;
                    } else if (days < 30) {
                      const weeks = Math.floor(days / 7);
                      diffText = `${weeks} week${weeks === 1 ? '' : 's'} ago`;
                    } else if (days < 365) {
                      const months = Math.floor(days / 30);
                      diffText = `${months} month${months === 1 ? '' : 's'} ago`;
                    } else {
                      const years = Math.floor(days / 365);
                      diffText = `${years} year${years === 1 ? '' : 's'} ago`;
                    }
                  }
                  return diffText;
                }

                let finalConvertedValue = convertedValue;
                // Onset에 대해 날짜 치환 적용
                if (
                  (questionText === 'Onset' || convertedQuestionText === 'Onset' || convertedQuestionText.includes('Datetime')) &&
                  typeof convertedValue === 'string' &&
                  (
                    convertedValue.match(/^\d{4}-\d{2}-\d{2}$/) ||
                    convertedValue.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/) ||
                    convertedValue.match(/^\d{4}-\d{2}-\d{2} \d{2}$/)
                  )
                ) {
                  finalConvertedValue = humanizeDateDifference(convertedValue);
                }

                let finalConvertedQuestionText = convertedQuestionText;
                if (convertedQuestionText.includes('Datetime')) {
                  finalConvertedQuestionText = convertedQuestionText.replace('Datetime', 'Onset');
                }

                hpiData.push(`${convertedSymptomName} - ${convertedQuestionText}: ${finalConvertedValue}`);
              }
            }

            // answerKeys가 있는 경우도 처리 (선택된 옵션들)
            if (answer?.answerKeys && answer.answerKeys.length > 0) {
              answer.answerKeys.forEach((key: string) => {
                if (key.startsWith('o-')) {
                  const optionText = key.replace('o-', '').replace(/-/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());

                  // 질문 키를 사람이 읽기 쉬운 형태로 변환
                  let questionText = questionKey.replace('q-', '').replace(/-/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());

                  // 특정 질문들을 더 명확하게 변환
                  if (questionKey === 'q-sx-continuous-intermittent') questionText = 'Nature';
                  else if (questionKey === 'q-chest-location') questionText = 'Location';
                  else if (questionKey === 'q-abrupt-gradual') questionText = 'Onset Type';
                  else if (questionKey === 'q-chest-event') questionText = 'Context';
                  else if (questionKey === 'q-sx-aggravation') questionText = 'Progression';

                  // 의료 줄임말 변환 적용
                  const convertedSymptomName = convertMedicalAbbreviation(symptomName);
                  const convertedQuestionText = convertMedicalAbbreviation(questionText);
                  const convertedOptionText = convertMedicalAbbreviation(optionText);

                  hpiData.push(`${convertedSymptomName} - ${convertedQuestionText}: ${convertedOptionText}`);
                }
              });
            }
          });
        }
      }
    });

    // cc, pmh, allergy, hpi, medication 데이터는 위에서 추출한 값 사용
    // promptInfoHpi, treeStatesInfo는 이 context에 없으므로 hpiData, pmhData, allergyData, medicationData로 대체

    const ageString = (() => {
      if (age !== null && age >= 2) {
        return `${age} years old`;
      }
      if (monthAge !== null && monthAge >= 1) {
        return `${monthAge} months old`;
      }
      if (dayAge !== null && monthAge < 1) {
        return `${dayAge} days old`;
      }
      return 'Age is Not provided';
    })();

    const isKorean = container.defaultLanguage === 'ko';

    // descriptive note prompt 생성 (DescriptiveButton.tsx와 동일한 형식)
    const englishPrompt = `
You are an expert medical scribe specializing in clinical documentation. Your task is to transform the following raw, patient-generated clinical data into a formal History of Present Illness (HPI) note.
The HPI must be a clear, concise, and chronologically organized narrative written in professional medical prose. Follow these specific principles:
1. Structure of the HPI:
- Opening Statement: The first sentence is critical. It must introduce the patient by stating their age, gender, any relevant past medical history, and their chief complaint. If Past Medical History is not provided or empty, do not mention it.
Example: "The patient is a 34-year-old male with no relevant past medical history who presents for evaluation following a motor vehicle collision."
However, If the age of the patient is below 2, describe age in months instead of years. In addition, if monthAge is provided, use it instead of age.
- Narrative Body: After the opening, build a chronological story of the events. Integrate the details of the chief complaint using the OLDCARTS framework as a guide to ensure completeness:
Onset: When did it start? (DO NOT USE 'Approximately' in the answer)
Location: Where is the symptom?
Duration: How long has it been going on?
Character: What does the symptom feel like?
Aggravating/Alleviating Factors: What makes it better or worse?
Radiation: Does the pain or symptom travel?
Timing: Is it constant or intermittent?
Severity: How bad is it?
Associated Symptoms & Context: Weave in any related details, such as the mechanism of injury, treatments attempted, and pertinent negatives (the absence of key symptoms, like denying head trauma).
2. MUST WRITE IN THE FOLLOWING ORDER:
(1) Opening Statement (First sentence)
(2) Narrative Body (After the opening, build a chronological story of the events. Integrate the details of the chief complaint using the OLDCARTS framework as a guide to ensure completeness)
(3) If there is a ‘Medication Prior to Admission’, mention it in the last sentence of the narrative body.
(4) Do not write ‘DHx’ in narrative form. If provided, list them at the end as follows, If there is no medication, mention it ‘None’:
Example:
Medication: Lisinopril, Tylenol
(5) Do not write Allergy in narrative form. If provided, list them at the end as follows, If there is no allergy, mention it 'None':
Example:
Allergy: Bacmin, Cat'claw
(6) If there is a language other than English, document it like the following example. Example) The patient is a 44-year-old female who presents with abdominal pain [Auto-translated from Korean 배가 아파요]. for his symptoms 6 hours prior to presentation.
3. Rules for Content:
Synthesize, Don't List: Do not simply list the data points. Weave them into a coherent paragraph.
No New Information: You must not add any information or make assumptions that are not explicitly provided in the data below.
Use All Information: You must incorporate every piece of provided data into your narrative.
Avoid Redundancy: Each symptom or detail should be mentioned only once unless it adds new temporal or qualitative information. Do not repeat symptoms with only minor wording changes.
Preserve Logical Flow: Do not imply causal relationships between symptoms unless such relationships are explicitly stated. Avoid suggesting that one symptom caused or worsened another unless that is clearly indicated in the input.
Maintain Chronological and Thematic Clarity: Organize symptoms first chronologically, and secondarily by system (e.g., respiratory, gastrointestinal, genitourinary). If multiple symptoms begin on the same day, group them by system for readability.
Professional Language: Convert abbreviations (like M, F, CC, PMHx) and patient-friendly language into a formal clinical narrative. Use medically appropriate terminology and tone throughout.
Raw Clinical Data to Process:
${gender} / ${ageString}
CC
${cc}
===
Past Medical History:
${pmhData.join('\n')}
===
HPI:
${hpiData.join('\n')}
===
Task:
Now, generate the HPI note based on the data and instructions provided.
    `.trim();

    const languageInstruction = isKorean
      ? 'Write the final output in Korean.'
      : 'Write the final output in English.';

    const safetyInstruction =
      'Do not add or assume any information that is not explicitly provided. Do not invent symptoms. Especially do not include hallucinations.';

    const prompt = `${englishPrompt}\n\n${languageInstruction}\n${safetyInstruction}`;


    const generateAndSaveDescriptiveNote = async () => {
      try {
        console.log('[PaSurveyEndPage] Generating descriptive note for non-EMR visit');

        // visitId를 여러 소스에서 가져옴 (useData의 visit 또는 useSurveyState의 visit)
        const visitId = visit?.uuid || visitFromSurveyState?.uuid;
        if (!visitId) {
          console.warn('[generateAndSaveDescriptiveNote] visitId not found - cannot generate descriptive note');
          return;
        }

        // Gemini를 통해 descriptive report 생성
        const result = await generateDescriptiveReport({
          variables: {
            visitId,
            prompt,
          },
        });

        const generatedText = result?.data?.generateDescriptiveReport?.text;

        if (generatedText && visit?.id) {
          console.log('[PaSurveyEndPage] Saving descriptive note to patient visit');

          // descriptive note만 업데이트 (reported 상태에서도 허용됨)
          // 백엔드에서 isDescriptiveNoteOnlyUpdate 체크를 통과하기 위해
          // surveyState.reportState.descriptiveReport만 포함
          await updatePatientVisit({
            variables: {
              id: visit.id,
              data: {
                surveyState: {
                  reportState: {
                    descriptiveReport: generatedText,
                  },
                },
              },
            },
          });

          // local state도 업데이트
          const updatedReportState = {
            ...reportState,
            descriptiveReport: generatedText,
          };
          setReportState(updatedReportState);
          console.log('[PaSurveyEndPage] Descriptive note saved successfully');
        }
      } catch (error) {
        console.error('[PaSurveyEndPage] Failed to generate/save descriptive note:', error);
      } finally {
        setDescriptiveNoteGenerated(true);
      }
    };

    generateAndSaveDescriptiveNote();
  }, [visit?.uuid, visit?.id, surveyState?.patientDetails, descriptiveNoteGenerated, reportState?.descriptiveReport]);

  const handleProviderAndPharmacy = async () => {
    const patientId = surveyState?.patientDetails?.patientid;
    const departmentId = surveyState?.patientDetails?.departmentid;
    const providerId = surveyState?.providerId;
    const pharmacyId = surveyState?.pharmacyId;

    if (pharmacyId) {
      await AthenaServices.putPharmacy(patientId, departmentId, pharmacyId);
    }

    if (providerId) {
      await AthenaServices.putProvider(patientId, departmentId, providerId);
    }
  }

  if (!survey || loadingTrans) {
    return (
      <div className='flex flex-1 w-full'>
        <LoadingAnim className='h-full p-10 w-full' />
      </div>
    );
  }

  return (
    <div className='flex-1 flex-col gap-5 justify-between'>
      <div className='flex-1 flex-col gap-5'>
        <PaTreeHeader showFullBar noBack />

        <div className='px-5'>
          <div className='flex-center'>
            <LogoPatientDynamic logoUrl={container.logoUrl} className='w-9' />
          </div>

          {!visit?.archived && (
            <div className='flex-center flex-col gap-4'>
              <>
                <div className='pt-12 pb-6 w-1/3 mx-auto'>
                  <img src={thumbsUp} className='block mx-auto' />
                </div>

                <div className='flex-col gap-2 flex-center-y'>
                  {(() => {
                    return (
                      <>
                        <h4 className='font-bold sm'>감사합니다!</h4>
                        <div className='text-base opacity-70 px-2 text-center'>

                        </div>
                      </>
                    );
                  })()}
                </div>
              </>
            </div>
          )}

          {!visit?.archived && isF1z7sna && (
            <div className='mt-6 space-y-4'>
              <div className='border-t border-border' />
              <HospitalFAQSection
                metaItems={metaItems}
                t={t}
                tOptions={tOptions}
                selectedFaqIndex={selectedFaqIndex}
                setSelectedFaqIndex={setSelectedFaqIndex}
              />

              <div className='bg-primary bg-opacity-5 rounded-2xl p-5 shadow-soft space-y-4'>
                <ClinicalTrialCard />
                <ClinicalTrialAds />
                <ClinicalTrialActions onWaitingList={handleWaitingList} />
              </div>
            </div>
          )}

          {visit?.archived && (
            <div className='flex-center flex-col gap-4 py-10'>
              <div className='flex-col gap-2 flex-center-y'>
                <div className='text-base opacity-70 px-2 text-center flex-1 bg-primary bg-opacity-10 rounded-xl w-full p-2'>
                  {sltStr({ key: 'finished-archived' })}
                </div>
              </div>
            </div>
          )}

          {!isF1z7sna && survey.access_restriction && survey.access_restriction !== Enum_Survey_Access_Restriction.ProviderOnly &&
            <div className='flex-center flex-col gap-4 py-10'>
              <div className='flex-col gap-2 flex-center-y'>
                <Button variant="contained"
                  onClick={() => {
                    setAllowNavigate(true);
                    nav(`/sp/${visit?.uuid}`);
                  }}
                >View Medical Provider Report</Button>
              </div>
            </div>

          }

          {/* FAQ */}
          {!isF1z7sna && (
            <HospitalFAQSection
              metaItems={metaItems}
              t={t}
              tOptions={tOptions}
              selectedFaqIndex={selectedFaqIndex}
              setSelectedFaqIndex={setSelectedFaqIndex}
            />
          )}
        </div>
      </div>

      <div className='hidden flex-none'>
        <Footer />
      </div>
      {isF1z7sna && (
        <ClinicalTrialInquiryModal
          open={isWaitingListOpen}
          onOpenChange={setIsWaitingListOpen}
          trialName='맞춤 임상시험'
        />
      )}
    </div>
  );
}

function PaSurveyEndPage() {
  return (
    <PatientOrgContainer.Provider>
      <PaSurveyEndPageContent />
    </PatientOrgContainer.Provider>
  );
}

export default memo(PaSurveyEndPage);
